import { Address } from './address';
import * as Yup from 'yup';
import { RecaptchaResponse } from '../components/form/recaptcha';

export async function createCase(data: Omit<SelfServiceFormValues, 'attachments'>): Promise<string> {
    const response = await fetch('/wp-json/mizkan/v1/enquiries/self-service', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw response;
    }

    const { caseId } = await response.json();

    return caseId;
}

export async function attachFile(caseId: string, file: File) {
    const data = new FormData();
    data.set('file', file);

    const response = await fetch(`/wp-json/mizkan/v1/enquiries/self-service/${caseId}/attachments`, {
        method: 'POST',
        body: data,
    });

    if (!response.ok) {
        throw response;
    }
}

export type SelfServiceFormValues = {
    origin: string,
    attachments: File[],
    firstName: string,
    lastName: string,
    email: {
        first: string,
        second: string,
    },
    address: Address & {
        manual: boolean,
        search: string,
        searchComplete: boolean,
    },
    productProblem: {
        product: string,
        batchCode: string,
        problem: string,
    },
    productEnquiry: {
        product: string,
        enquiry: string,
    },
    telephone: string,
    enquiryType: '' | EnquiryType,
    agreePrivacy: boolean,
    recaptcha: RecaptchaResponse,
};

export const enquiryTypeOptions = {
    'productProblem': 'Problem with a product',
    'productEnquiry': 'Product enquiry',
    'whereToBuy': 'Where to buy',
    'recipes': 'Recipes',
    'tradeEnquiry': 'Trade enquiry (including restaurants)',
    'workingWithUs': 'Working with us',
    'recycling': 'How to recycle our packaging',
};

export type EnquiryType = keyof typeof enquiryTypeOptions;

export function isSelfServiceEnquiry(enquiryType: EnquiryType) {
    const selfServiceEnquiryTypes: EnquiryType[] = [
        'whereToBuy',
        'recipes',
        'tradeEnquiry',
        'workingWithUs',
        'recycling',
    ];

    return selfServiceEnquiryTypes.includes(enquiryType);
}

export const validationSchema = Yup.object()
    .shape({
        firstName: Yup.string()
            .required('Your first name is required'),
        lastName: Yup.string()
            .required('Your last name is required'),
        email: Yup.object()
            .shape({
                first: Yup.string()
                    .email('Please enter a valid email address')
                    .required('Your email address is required'),
                second: Yup.string()
                    .required('Your email address is required')
                    .oneOf([
                        Yup.ref('first'),
                    ], 'Your email addresses do not match'),
            }),
        address: Yup.object()
            .shape({
                manual: Yup.boolean(),
                searchComplete: Yup.boolean().when('manual', {
                    is: false,
                    then: (schema) => schema.oneOf([true], 'Your address is required'),
                }),
                line1: Yup.string()
                    .required('Your address is required'),
                line2: Yup.string(),
                city: Yup.string()
                    .required('Your town or city is required'),
                county: Yup.string(),
                postcode: Yup.string()
                    .matches(/^[a-z]{1,2}\d{1,2}[a-z]?\s*\d[a-z]{2}$/i, 'Please enter a valid postcode')
                    .required('Your postcode is required'),
            }),
        enquiryType: Yup.string()
            .oneOf(Object.keys(enquiryTypeOptions), 'Please choose a valid enquiry type')
            .required('Please choose an enquiry type'),
        productProblem: Yup.object()
            .when('enquiryType', {
                is: 'productProblem',
                then: Yup.object()
                    .shape({
                        product: Yup.string()
                            .required('Please choose a product'),
                        batchCode: Yup.string()
                            .required('Please enter your product batch code'),
                        problem: Yup.string()
                            .required('Please tell us about the problem'),
                    }),
            }),
        productEnquiry: Yup.object()
            .when('enquiryType', {
                is: 'productEnquiry',
                then: Yup.object().shape({
                    product: Yup.string()
                        .required('Please choose a product'),
                    enquiry: Yup.string()
                        .required('Please tell us about your enquiry'),
                }),
            }),
        telephone: Yup.string()
            .test('valid-telephone', 'Please enter a valid phone number', (value, { createError }) => {
                if (!value) {
                    return createError({ message: 'Please enter your phone number' });
                }

                value = value.replace(/\s/g, '');

                return /^(0[1257]\d{9}|01\d{8})$/.test(value);
            }),
        agreePrivacy: Yup.boolean()
            .isTrue('Please accept the privacy policy'),
        recaptcha: Yup.string()
            .nullable()
            .required('Please confirm you are not a robot'),
    });
